import React, { useCallback, useContext, useMemo, useState } from 'react';

import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import { WeeklyWeekBreakdown } from 'components/DriverAX/providers/WeeklyBonusOverrideProvider';
import useBonusReasonCodes from 'components/DriverAX/hooks/useBonusReasonCodes';
import useValueFormatters from 'hooks/useValueFormatters';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

/**
 * The confirm-and-save surface for a staged bonus override: optional reason
 * code(s), an optional note, the previewed total-pay change, and Save/Cancel.
 * Defaults to reading everything from BonusOverviewContext (the single-record
 * entity-app / slideout flow), but the props let the per-week fact-table flow
 * drive it instead — supplying its own confirm handler, aggregate pay change,
 * program id (for reason codes), and initial note/codes.
 */
const ConfirmOverrideDialog = ({
  close,
  isNoteRequired,
  onConfirm,
  payChangeText: payChangeTextOverride,
  reasonCodeProgramId,
  initialNote,
  initialReasonCodes,
  appliesToWeeks,
  breakdown,
  formatPayValue,
}: {
  close: () => void;
  isNoteRequired: boolean;
  // Per-week flow overrides. When omitted the dialog uses BonusOverviewContext.
  onConfirm?: (note: string, reasonCodes: string[]) => Promise<unknown>;
  payChangeText?: string;
  reasonCodeProgramId?: string;
  initialNote?: string;
  initialReasonCodes?: string[];
  appliesToWeeks?: number;
  // Per-week breakdown of each changed week's rewards (weekly flow).
  // `formatPayValue` formats each raw reward value with its reward's field.
  breakdown?: WeeklyWeekBreakdown[];
  formatPayValue?: (value: number, field?: string) => string;
}) => {
  const { bonusOverview, onOverridesConfirmed } =
    useContext(BonusOverviewContext);
  const { formatField } = useValueFormatters();
  const [isLoading, setIsLoading] = useState(false);

  const confirm = onConfirm ?? onOverridesConfirmed;

  const [note, setNote] = useState<string>(
    () => initialNote ?? bonusOverview?.overridesNote ?? '',
  );

  // Reason codes are staff-managed per bonus program. When any are configured
  // the user must pick at least one; otherwise the picker is hidden and the
  // dialog behaves as before. Pre-fill from the record's persisted codes so
  // they stick across edits, and re-send whatever is selected.
  const availableReasonCodes = useBonusReasonCodes(
    reasonCodeProgramId ?? bonusOverview?.bonusProgram.id ?? '',
  );
  const [selectedReasonCodes, setSelectedReasonCodes] = useState<string[]>(
    () => initialReasonCodes ?? bonusOverview?.overrides?.reasonCodes ?? [],
  );
  const areReasonCodesConfigured = availableReasonCodes.length > 0;
  const isReasonCodeMissing =
    areReasonCodesConfigured && selectedReasonCodes.length === 0;

  const toggleReasonCode = useCallback((code: string) => {
    setSelectedReasonCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }, []);

  const reasonCodeOptions: DropdownOption[] = useMemo(
    () =>
      availableReasonCodes.map((reasonCode) => ({
        key: reasonCode.id,
        label: reasonCode.code,
        value: reasonCode.code,
        isSelected: selectedReasonCodes.includes(reasonCode.code),
        onSelected: () => toggleReasonCode(reasonCode.code),
      })),
    [availableReasonCodes, selectedReasonCodes, toggleReasonCode],
  );

  const contextPayChangeText = useMemo(() => {
    if (!bonusOverview || !bonusOverview.stagedOverridePayChange) {
      return;
    }

    const actualPayFormatted = formatField({
      field: 'totalPay',
      value: bonusOverview.stagedOverridePayChange.actualPay,
      dataset: bonusOverview.bonusProgram.dataset,
    });

    const previewPayFormatted = formatField({
      field: 'totalPay',
      value: bonusOverview.stagedOverridePayChange.previewPay,
      dataset: bonusOverview.bonusProgram.dataset,
    });

    return `${actualPayFormatted} -> ${previewPayFormatted}`;
  }, [bonusOverview, formatField]);

  const payChangeText = payChangeTextOverride ?? contextPayChangeText;

  // Formats a breakdown value; the weekly flow passes the pay field's formatter.
  const formatPay: (value: number, field?: string) => string =
    formatPayValue ?? ((value: number) => `${value}`);

  const onSaveClicked = useCallback(() => {
    setIsLoading(true);
    confirm(note, selectedReasonCodes).then(() => {
      setIsLoading(false);
      close();
    });
  }, [close, note, confirm, selectedReasonCodes]);

  return (
    <div
      style={{
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        // Bound the dialog to the space the InlineDialog actually has on its
        // chosen side (published as a CSS var) so a long per-week breakdown
        // scrolls internally instead of overflowing off the top of the screen.
        maxHeight: 'calc(var(--inline-dialog-available-height, 100vh) - 16px)',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Typography.Header type={'H3'}>Confirm Override</Typography.Header>
      </div>
      <div
        style={{
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          paddingRight: 12,
        }}
      >
        {areReasonCodesConfigured && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              <Typography.Body type={'Label'}>
                Select reason code(s)
              </Typography.Body>
            </div>
            <Inputs.Dropdown
              testId={'reason-code-dropdown'}
              isMulti
              fullWidth
              placeholder={'Select reason code(s)'}
              hasError={isReasonCodeMissing}
              options={reasonCodeOptions}
            />
          </div>
        )}
        {isNoteRequired && (
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                marginBottom: 4,
              }}
            >
              <Typography.Body type={'Label'}>
                Add or update the reason for your override
              </Typography.Body>
            </div>
            <Inputs.TextArea
              data-testid={'override-note-input'}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </div>
        )}
        <div
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            gap: 4,
            padding: 16,
            marginBottom: 16,
          }}
        >
          {breakdown && breakdown.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {breakdown.map((week) => (
                <div
                  key={week.startDate}
                  style={{ marginBottom: 8 }}
                  data-testid={`breakdown-week-${week.startDate}`}
                >
                  <Typography.Body type={'Label'}>{week.label}</Typography.Body>
                  {week.changes.map((change) => (
                    <Row key={change.rewardName} spaceBetween centerAlign>
                      <Typography.Body type={'Body 13'}>
                        {change.label}
                      </Typography.Body>
                      <Typography.Body type={'Body 13'}>
                        {change.isChanged
                          ? `${formatPay(
                              change.actual,
                              change.field,
                            )} → ${formatPay(change.preview, change.field)}`
                          : formatPay(change.actual, change.field)}
                      </Typography.Body>
                    </Row>
                  ))}
                </div>
              ))}
            </div>
          )}
          <Typography.Body type={'Label'}>
            Total Pay change due to override:
          </Typography.Body>
          <Typography.Header type={'H3'}>{payChangeText}</Typography.Header>
          {appliesToWeeks !== undefined && appliesToWeeks > 0 && (
            <Typography.Body type={'Label'}>
              {`Applies to ${appliesToWeeks} week${
                appliesToWeeks > 1 ? 's' : ''
              }`}
            </Typography.Body>
          )}
        </div>
      </div>
      <Row style={{ justifyContent: 'flex-end', gap: '8px', marginTop: 16 }}>
        <Button
          size={'Small'}
          type={'Secondary'}
          onClick={close}
          label={'Cancel'}
        />
        <Button
          size={'Small'}
          type={'Primary'}
          onClick={onSaveClicked}
          label={'Save Override(s)'}
          isLoading={isLoading}
          isDisabled={isReasonCodeMissing}
          isDisabledMessage={
            isReasonCodeMissing ? 'Select a reason code to continue' : undefined
          }
        />
      </Row>
    </div>
  );
};

export default ConfirmOverrideDialog;
